import { computed, nextTick, ref } from 'vue';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';
import { composerHtmlToPlainText } from '@/components/lead_xpress/aiDraft/draftBodyHtml.ts';
import { emailAiDraftApiAdapter } from '@/components/lead_xpress/aiDraft/emailAiDraftApiAdapter.ts';
import { mockEmailAiDraftAdapter } from '@/components/lead_xpress/aiDraft/mockEmailAiDraftAdapter.ts';
import {
    EmailAiDraftState,
    isEmailAiDraftCancelledResult,
    isEmailAiDraftUnsupportedResult,
} from '@/components/lead_xpress/aiDraft/types.ts';
import { DraftFeedback } from '@/entities/enums/DraftFeedback.ts';
import { useAiDraftFeedback } from '@/components/aiDraft/feedback/useAiDraftFeedback.ts';
import type {
    EmailAiDraftAdapter,
    EmailAiDraftGeneratedDraft,
    EmailAiDraftLocale,
    EmailAiDraftLocaleSnapshot,
    EmailAiDraftSnapshot,
    EmailAiDraftStopPayload,
} from '@/components/lead_xpress/aiDraft/types.ts';

export { EmailAiDraftState } from '@/components/lead_xpress/aiDraft/types.ts';
export { DraftFeedback } from '@/entities/enums/DraftFeedback.ts';
export type {
    EmailAiDraftAdapter,
    EmailAiDraftFeedbackSubmission,
    EmailAiDraftGeneratedDraft,
    EmailAiDraftGeneratePayload,
    EmailAiDraftGenerateResult,
    EmailAiDraftLocale,
    EmailAiDraftLocaleSnapshot,
    EmailAiDraftSnapshot,
    EmailAiDraftStopPayload,
    EmailAiDraftUpdatePayload,
    EmailAiDraftUpdateResult,
} from '@/components/lead_xpress/aiDraft/types.ts';

interface GenerateOptions {
    locale: EmailAiDraftLocale;
    leadId: number;
    existingSubject: string | null;
    snapshot: EmailAiDraftSnapshot;
    applyDraft: (draft: EmailAiDraftGeneratedDraft) => boolean;
}

interface UpdateOptions {
    locale: EmailAiDraftLocale;
    leadId: number;
    snapshot: EmailAiDraftSnapshot;
    subject: string;
    body: string;
    instruction: string;
    applyDraft: (draft: EmailAiDraftGeneratedDraft) => boolean;
}

interface UseEmailAiDraftOptions {
    adapter?: EmailAiDraftAdapter;
    requestIdGenerator?: () => string;
}

interface CompleteDraftOptions {
    applyDraft: (draft: EmailAiDraftGeneratedDraft) => boolean;
    currentRequestId: number;
    locale: EmailAiDraftLocale;
    nextGeneratedDraft: EmailAiDraftGeneratedDraft;
    nextSnapshot: EmailAiDraftSnapshot;
}

interface PreviousEmailDraftState {
    feedback: DraftFeedback | null;
    generatedDraft: EmailAiDraftGeneratedDraft | null;
    hasGeneratedDraft: boolean;
}

interface DraftRequestHandle {
    currentRequestId: number;
    previousDraftState: PreviousEmailDraftState;
}

const isRateLimitError = (error: unknown): error is Error & { rateLimitSeconds: number } => {
    return typeof (error as { rateLimitSeconds?: unknown })?.rateLimitSeconds === 'number';
};

const DEFAULT_RATE_LIMIT_SECONDS = 60;

const SavedInteractionIdValueSchema = z.number().int().positive().nullable();
const SavedInteractionIdSchema = z.unknown().transform((input) => {
    const parsed = SavedInteractionIdValueSchema.safeParse(input);
    return parsed.success ? parsed.data : null;
});
const SavedInteractionIdsObjectSchema = z.object({
    en: SavedInteractionIdSchema,
    fr: SavedInteractionIdSchema,
});
const SavedInteractionIdsSchema = z.unknown().transform((input) => {
    const parsed = SavedInteractionIdsObjectSchema.safeParse(input);
    return parsed.success ? parsed.data : { en: null, fr: null };
});

const subjectStartsWithReply = (subject: string) => /^\s*re:/i.test(subject);

const createEmailAiDraftRequestId = () => uuidv4();

const defaultEmailAiDraftAdapter: EmailAiDraftAdapter = {
    ...mockEmailAiDraftAdapter,
    generate: emailAiDraftApiAdapter.generate,
    update: emailAiDraftApiAdapter.update,
    stop: emailAiDraftApiAdapter.stop,
};

const buildLocaleUndoSnapshot = (
    locale: EmailAiDraftLocale,
    nextSnapshot: EmailAiDraftSnapshot
): EmailAiDraftLocaleSnapshot => ({
    locale,
    subject: nextSnapshot.subject[locale] ?? '',
    body: nextSnapshot.body[locale] ?? '',
});

export const useEmailAiDraft = (options: UseEmailAiDraftOptions = {}) => {
    const adapter = options.adapter ?? defaultEmailAiDraftAdapter;
    const requestIdGenerator = options.requestIdGenerator ?? createEmailAiDraftRequestId;
    const state = ref<EmailAiDraftState>(EmailAiDraftState.Idle);
    const snapshot = ref<EmailAiDraftSnapshot | null>(null);
    const undoSnapshotByLocale = ref<
        Partial<Record<EmailAiDraftLocale, EmailAiDraftLocaleSnapshot>>
    >({});
    const localRequestSequence = ref(0);
    const activeBackendRequest = ref<EmailAiDraftStopPayload | null>(null);
    const activeRequestLocale = ref<EmailAiDraftLocale | null>(null);
    const rateLimitSeconds = ref(DEFAULT_RATE_LIMIT_SECONDS);
    const feedback = ref<DraftFeedback | null>(null);
    const hasGeneratedDraft = ref(false);
    const generatedDraft = ref<EmailAiDraftGeneratedDraft | null>(null);
    const interactionIdByLocale = ref<Record<EmailAiDraftLocale, number | null>>({
        en: null,
        fr: null,
    });

    const { clearFeedbackTarget, setFeedbackTarget, selectFeedback, submitFeedback } =
        useAiDraftFeedback(adapter, feedback);

    const isInProgress = computed<boolean>(() => state.value === EmailAiDraftState.Loading);
    const canUndo = computed<boolean>(
        () =>
            Object.keys(undoSnapshotByLocale.value).length > 0 &&
            state.value !== EmailAiDraftState.Loading
    );
    const canUndoForLocale = (locale: EmailAiDraftLocale) =>
        canUndo.value && undoSnapshotByLocale.value[locale] !== undefined;

    const clearGeneratedDraft = () => {
        generatedDraft.value = null;
        hasGeneratedDraft.value = false;
    };

    const clearActiveBackendRequest = (currentRequestId?: number) => {
        if (currentRequestId === undefined || localRequestSequence.value === currentRequestId) {
            activeBackendRequest.value = null;
            activeRequestLocale.value = null;
        }
    };

    const startDraftRequest = (
        nextSnapshot: EmailAiDraftSnapshot,
        nextActiveBackendRequest: EmailAiDraftStopPayload,
        locale: EmailAiDraftLocale,
        preserveFeedbackTarget = false
    ): DraftRequestHandle => {
        const currentRequestId = localRequestSequence.value + 1;
        const previousDraftState = {
            feedback: feedback.value,
            generatedDraft: generatedDraft.value,
            hasGeneratedDraft: hasGeneratedDraft.value,
        };

        localRequestSequence.value = currentRequestId;
        activeBackendRequest.value = nextActiveBackendRequest;
        activeRequestLocale.value = locale;
        snapshot.value = nextSnapshot;
        state.value = EmailAiDraftState.Loading;
        feedback.value = null;
        clearGeneratedDraft();

        if (!preserveFeedbackTarget) {
            clearFeedbackTarget();
        }

        return { currentRequestId, previousDraftState };
    };

    const requestIsActive = (currentRequestId: number) =>
        localRequestSequence.value === currentRequestId &&
        state.value !== EmailAiDraftState.Stopped;

    const handleBlockedApplyDraft = (currentRequestId: number) => {
        snapshot.value = null;
        clearActiveBackendRequest(currentRequestId);
        clearGeneratedDraft();
        clearFeedbackTarget();
        state.value = EmailAiDraftState.Idle;
    };

    const handleCancelledDraftRequest = (currentRequestId: number) => {
        snapshot.value = null;
        clearActiveBackendRequest(currentRequestId);
        clearGeneratedDraft();
        clearFeedbackTarget();
        state.value = EmailAiDraftState.Stopped;
    };

    const handleUnsupportedDraftRequest = (
        currentRequestId: number,
        previousDraftState: PreviousEmailDraftState
    ) => {
        snapshot.value = null;
        clearActiveBackendRequest(currentRequestId);
        feedback.value = previousDraftState.feedback;
        generatedDraft.value = previousDraftState.generatedDraft;
        hasGeneratedDraft.value = previousDraftState.hasGeneratedDraft;
        state.value = EmailAiDraftState.UpdateUnsupported;
    };

    const cancelBackendRequest = (requestToCancel: EmailAiDraftStopPayload) => {
        Promise.resolve(adapter.stop(requestToCancel)).catch(() => {});
    };

    const completeDraftRequest = async ({
        applyDraft,
        currentRequestId,
        locale,
        nextGeneratedDraft,
        nextSnapshot,
    }: CompleteDraftOptions) => {
        if (!applyDraft(nextGeneratedDraft)) {
            handleBlockedApplyDraft(currentRequestId);

            return;
        }

        await nextTick();

        if (!requestIsActive(currentRequestId)) {
            return;
        }

        undoSnapshotByLocale.value[locale] = buildLocaleUndoSnapshot(locale, nextSnapshot);
        generatedDraft.value = nextGeneratedDraft;
        hasGeneratedDraft.value = true;
        interactionIdByLocale.value[locale] =
            nextGeneratedDraft.interactionId > 0 ? nextGeneratedDraft.interactionId : null;
        feedback.value = null;
        clearFeedbackTarget();
        setFeedbackTarget(nextGeneratedDraft.interactionId);
        state.value = EmailAiDraftState.Inserted;
        snapshot.value = null;
        clearActiveBackendRequest(currentRequestId);
    };

    const handleDraftRequestError = (error: unknown, currentRequestId: number) => {
        if (localRequestSequence.value !== currentRequestId) {
            return;
        }

        clearActiveBackendRequest(currentRequestId);
        clearGeneratedDraft();
        clearFeedbackTarget();

        if (isRateLimitError(error)) {
            rateLimitSeconds.value = error.rateLimitSeconds;
            state.value = EmailAiDraftState.RateLimited;

            return;
        }

        state.value = EmailAiDraftState.Failed;
    };

    const generate = async ({
        locale,
        leadId,
        existingSubject,
        snapshot: nextSnapshot,
        applyDraft,
    }: GenerateOptions) => {
        const backendRequestId = requestIdGenerator();
        const { currentRequestId } = startDraftRequest(
            nextSnapshot,
            {
                leadId,
                requestId: backendRequestId,
            },
            locale
        );

        try {
            const draft = await adapter.generate({
                locale,
                leadId,
                requestId: backendRequestId,
                existingSubject,
            });

            if (!requestIsActive(currentRequestId)) {
                clearActiveBackendRequest(currentRequestId);

                return;
            }

            if (isEmailAiDraftCancelledResult(draft)) {
                handleCancelledDraftRequest(currentRequestId);

                return;
            }

            const useGeneratedSubject =
                !subjectStartsWithReply(nextSnapshot.subject[locale] || '') && !!draft.subject;

            const nextGeneratedDraft = {
                ...draft,
                locale,
                useGeneratedSubject,
            };

            await completeDraftRequest({
                applyDraft,
                currentRequestId,
                locale,
                nextGeneratedDraft,
                nextSnapshot,
            });
        } catch (error) {
            handleDraftRequestError(error, currentRequestId);
        }
    };

    const update = async ({
        locale,
        leadId,
        snapshot: nextSnapshot,
        subject,
        body,
        instruction,
        applyDraft,
    }: UpdateOptions) => {
        const backendRequestId = requestIdGenerator();
        const { currentRequestId, previousDraftState } = startDraftRequest(
            nextSnapshot,
            {
                leadId,
                requestId: backendRequestId,
            },
            locale,
            true
        );

        try {
            // Strip composer markup so the model prompt gets plain text, not literal tags.
            const draft = await adapter.update?.({
                locale,
                leadId,
                requestId: backendRequestId,
                subject,
                body: composerHtmlToPlainText(body),
                instruction,
            });

            if (!draft) {
                throw new Error('Update with AI adapter is unavailable');
            }

            if (!requestIsActive(currentRequestId)) {
                clearActiveBackendRequest(currentRequestId);

                return;
            }

            if (isEmailAiDraftCancelledResult(draft)) {
                handleCancelledDraftRequest(currentRequestId);

                return;
            }

            if (isEmailAiDraftUnsupportedResult(draft)) {
                handleUnsupportedDraftRequest(currentRequestId, previousDraftState);

                return;
            }

            const useGeneratedSubject =
                !subjectStartsWithReply(nextSnapshot.subject[locale] || '') && !!draft.subject;
            const nextGeneratedDraft = {
                locale,
                subject: draft.subject ?? nextSnapshot.subject[locale] ?? '',
                body: draft.body,
                interactionId: draft.interactionId,
                traceId: draft.traceId,
                useGeneratedSubject,
            };

            await completeDraftRequest({
                applyDraft,
                currentRequestId,
                locale,
                nextGeneratedDraft,
                nextSnapshot,
            });
        } catch (error) {
            handleDraftRequestError(error, currentRequestId);
        }
    };

    const stop = async (restoreSnapshot: (snapshot: EmailAiDraftSnapshot) => void) => {
        const requestToCancel = activeBackendRequest.value;
        const snapshotToRestore = snapshot.value;

        localRequestSequence.value += 1;
        clearActiveBackendRequest();
        clearFeedbackTarget();

        if (!snapshotToRestore && !requestToCancel) {
            return;
        }

        if (snapshotToRestore) {
            restoreSnapshot(snapshotToRestore);
        }

        snapshot.value = null;
        clearGeneratedDraft();
        clearFeedbackTarget();
        state.value = EmailAiDraftState.Stopped;

        if (!requestToCancel) {
            return;
        }

        try {
            await adapter.stop(requestToCancel);
        } catch {
            // Stop is local-first; backend cancel failures must not undo the restored composer state.
        }
    };

    const undo = (
        locale: EmailAiDraftLocale,
        restoreSnapshot: (snapshot: EmailAiDraftLocaleSnapshot) => void
    ) => {
        const previousDraft = undoSnapshotByLocale.value[locale];

        if (!previousDraft || state.value === EmailAiDraftState.Loading) {
            return;
        }

        const ownsVisibleDraft = generatedDraft.value?.locale === locale;

        localRequestSequence.value += 1;
        restoreSnapshot(previousDraft);
        delete undoSnapshotByLocale.value[locale];
        snapshot.value = null;
        clearActiveBackendRequest();
        interactionIdByLocale.value[locale] = null;

        if (!ownsVisibleDraft) {
            return;
        }

        feedback.value = null;
        clearGeneratedDraft();
        clearFeedbackTarget();
        state.value = EmailAiDraftState.Idle;
    };

    const canResetLocale = (locale: EmailAiDraftLocale): boolean => {
        const hasOtherPendingRequest =
            activeRequestLocale.value !== null && activeRequestLocale.value !== locale;
        const ownsVisibleDraft = generatedDraft.value?.locale === locale;
        const ownsPendingRequest = activeRequestLocale.value === locale;

        return !hasOtherPendingRequest && (ownsVisibleDraft || ownsPendingRequest);
    };

    const reset = (preserveGeneratedDraft = false, locale: EmailAiDraftLocale | null = null) => {
        if (locale && !preserveGeneratedDraft) {
            interactionIdByLocale.value[locale] = null;
            delete undoSnapshotByLocale.value[locale];
        }

        if (locale && !canResetLocale(locale)) {
            return;
        }
        const requestToCancel = activeBackendRequest.value;
        const wasLoading = state.value === EmailAiDraftState.Loading;

        localRequestSequence.value += 1;
        state.value = EmailAiDraftState.Idle;
        feedback.value = null;

        if (!preserveGeneratedDraft) {
            if (!locale || generatedDraft.value?.locale === locale) {
                clearGeneratedDraft();
                clearFeedbackTarget();
            }
            if (!locale) {
                interactionIdByLocale.value = { en: null, fr: null };
            }
        }
        snapshot.value = null;
        if (!locale && !preserveGeneratedDraft) {
            undoSnapshotByLocale.value = {};
        }
        clearActiveBackendRequest();
        rateLimitSeconds.value = DEFAULT_RATE_LIMIT_SECONDS;

        if (wasLoading && requestToCancel) {
            cancelBackendRequest(requestToCancel);
        }
    };

    const restoreInteractionIds = (input: unknown) => {
        reset();
        interactionIdByLocale.value = SavedInteractionIdsSchema.parse(input);
    };

    return {
        canUndo,
        canUndoForLocale,
        feedback,
        generate,
        generatedDraft,
        hasGeneratedDraft,
        interactionIdByLocale,
        isInProgress,
        rateLimitSeconds,
        reset,
        restoreInteractionIds,
        selectFeedback,
        state,
        stop,
        submitFeedback,
        undo,
        update,
    };
};
